import $ from 'jquery'
import _ from 'lodash'
import FilterWizard from '../filters/filter-wizard'
import filterText from './filter-text'
import FilterBadges from 'scoreboard/components/filter-badges'
import assignLinkGroups from 'scoreboard/components/assign-link-groups'
import i18n from 'scoreboard/components/i18n'
import deepEqual from 'deep-equal'
import SingleFormfieldDialog from 'scoreboard/components/single-formfield-dialog'
import appDisplay from 'scoreboard/components/app-display'
import rollupFilterDropdown from 'scoreboard/datasets/filters/rollup-filter-dropdown'
import { filterNumericValues } from 'scoreboard/datasets/filters/filter-numeric-values'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import wrapModel from 'scoreboard/components/wrap-model'
import Dataset from 'scoreboard/datasets/model/dataset'

/**
 * Renders badges for dataset filters.  These are displayed on the Datasets Records
 * screen and in Admin | Groups | Datasets.
 */
var DatasetFilterBadges = FilterBadges.extend({

	/** @type {Dataset} */
	model: null,

	/**
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model a Dataset
	 *
	 * @param {Array} options.filters the filters being displayed
	 *
	 * @param {Object} options.treeItemsById a hash of tree items keyed by ID
	 *
	 * @param {String} options.noneMessage a message to display if there are no
	 * filters
	 *
	 * @param {Boolean} [options.isEditable=true] indicates whether the display can
	 * be edited, but does not determine if it is deletable
	 *
	 * @param {Boolean} options.showPermissionFilters whether we should show
	 * permission filters
	 *
	 * @param {Boolean} [options.showAggregationFilters=false] whether we should
	 * show filters that apply to aggregations (aka columns)
	 *
	 * @param {String} [options.allowAppCalendar] whether "Current Calendar"
	 * should be an option when working with date filters
	 *
	 * @param {String} [options.section] the section that this dataset is being
	 * displayed in ('dashboards' or 'datasets')
	 *
	 * @param {Boolean} [options.allLinkedFields=false] whether to show all
	 * linked fields rather than just the explicitly defined visible linked
	 * fields when building filters
	 *
	 * @param {Object} [options.filterTextOptions] options to pass through to filterText()
	 *
	 * @param {Object<Number, Object>} [options.datasetsById] the attributes of
	 * any relevant datasets, keyed by ID
	 *
	 * @param {Object<Number, Object>} [options.fieldsById] the attributes of
	 * any relevant dataset fields, keyed by dataset field ID
	 *
	 * @param {Boolean} [options.includeEditFields=true] whether to include the
	 * "Record is Editable" and "Record Edited" fields in the dropdown (#54607)
	 */
	initialize: function(options) {
		FilterBadges.prototype.initialize.apply(this, arguments)
		this.options = options || {}
		this.model = wrapModel(this.options.model, Dataset)
		this.filters = this.options.filters || []
		this.includeEditFields = this.options.includeEditFields ?? true
		this.treeItemsById = _.cloneDeep(_.get(this.options, 'treeItemsById', {}))
		this.showAggregationFilters = _.isNil(options?.showAggregationFilters) ?
			false : options.showAggregationFilters
		this.allLinkedFields = _.get(this.options, 'allLinkedFields',
			false)
		// #14071 be sure to set the linkGroups on whatever filters we're working
		// with.  It might be the top level dataset filters or the filters for a
		// particular permission
		assignLinkGroups(this.filters)
		this.fieldsById = this.options.fieldsById ||
			_.get(this.model, 'combinedFieldsById') || {}

		this.options.filtersByLinkGroup = () => {
			this.filteredFilters = _(this.filters)
				.filter((filter) =>
					// when a field is added on the overview, we aren't really
					// filtering on anything, so it doesn't count as an active
					// filter
					!_.isEmpty(filter.operator)
				)
				.sortBy('linkGroup')
				.value()

			return _(this.filteredFilters)
				.map((filter) => {
					var field = this.fieldsById[filter.fieldId]
					// if the filter is invalid, and we provided filterText explaining
					// why it's invalid, then don't run filterText on it
					let ft = filter.filterText
					if (_.isNil(filter.filterText)) {
						// this needs to go before checking if the filter is invalid
						// because filter-text will mark any filters it finds invalid
						ft = filterText(this.model, filter, field,
							this.treeItemsById, this.options.filterTextOptions)
					}
					return _.extend({}, filter, {
						label: this.getFilterLabel(filter, field),
						filterText: ft,
						valid: _.isNil(filter.valid) || filter.valid
					})
				})
				.groupBy('linkGroup')
				.value()
		}

		_.each(this.filters, f => {
			if (f.treeItems) _.each(f.treeItems, ti =>
				this.treeItemsById[ti.id] = ti)
		})
	},

	parseFilters: function(filter) {
		const field = this.fieldsById[filter.fieldId]

		// #42615 get rid of all other values that round to the same thing
		if (isNumericDataType(field?.dataType)) {
			filter.values = filterNumericValues(filter.values, field)
			if (!filter.valid && _.isNil(filter.filterText)) {
				filter.filterText = filterText(this.model, filter, field,
					this.treeItemsById, this.options.filterTextOptions)
			}
		}

		if (!_.isNil(filter.matching)) {
			// #44510 remove the matching property so we don't display it in front of
			// the filter's text. filter-text.js will handle putting the suffix in, if needed.
			// f is a deep copy so this doesn't modify the actual filter.
			delete filter.matching
		}

	},

	/**
	 * @return {Dataset} the dataset with the given ID
	 */
	getDataset: function(datasetId) {
		let dataset = this.model.datasetsById[datasetId]
		if (_.isNil(dataset)) {
			dataset = this.options.datasetsById[datasetId]
		}
		dataset = wrapModel(dataset, Dataset)
		return dataset
	},

	getFilterLabel: function(filter, field) {
		// #57798 there are some areas of the app where the valid check is not
		// necessary and not performed, so check whether filter.valid has
		// explicitly been declared false
		if (filter.valid === false) {
			return filter.filterText
		}

		// #54607 fallback to this.model.id for pseudo-fields that aren't
		// specific to a particular dataset
		let datasetId = field.datasetId ?? this.model.get('rootDatasetId')
		const dataset = this.getDataset(datasetId)
		const filterFieldText = dataset.getFieldLabelByIds(filter) ?? field.label
		const linkedDatasets = this.model.get('linkedDatasets')
		if (_.isEmpty(linkedDatasets)) return filterFieldText

		// #44512 #45254 show the dataset label only for filters that are not in
		// the main dataset
		if (filter.datasetId == this.model.get('rootDatasetId')) {
			return filterFieldText
		}

		// Escaping is handled by handlebars template
		return `${filterFieldText} (${dataset.get('label')})`
	},

	getDatasetsById: function() {
		return _.defaultTo(this.options.datasetsById, this.model.datasetsById)
	},

	addFilter: function(options) {
		// cant add a filter if we don't have a dataset to get fields from
		if (_.isNil(this.model)) return

		const wizard = new FilterWizard(_.defaults(options, this._wizardOptions(), {
			filters: this.filters,
			lockField: false,
			filter: {},
			allLinkedFields: this.allLinkedFields,
			includeEditFields: this.includeEditFields,
			showPermissionFilters: this.options.showPermissionFilters
		})).start()
		wizard.on('done', function(filter, treeItems) {
			filter.treeItems = treeItems
			this.updateTreeItems(treeItems)
			this.filters.push(filter)
			this.trigger('add-filter', filter, treeItems)
			this.render()
		}.bind(this))

		return wizard
	},

	clickFilter: function(e) {
		if (!this.isEditable) {
			return
		}

		const oldFilter = this.getFilterForEvent(e)
		const filtersNotBeingEdited = _.filter(this.filters, filter =>
			!deepEqual(filter, oldFilter))

		if (this.model.isFilterBuiltInRollupTemplateFilter(oldFilter)) {
			this._editBuiltInTemplateFilter(oldFilter)
		} else {
			this._editFilterUsingWizard(filtersNotBeingEdited, oldFilter)
		}
	},

	_editBuiltInTemplateFilter: function(filter) {
		if (!this.isEditable) {
			return
		}

		const {treeIds, rollupIsAnyFilters, fieldId} = filter
		const treeId = treeIds[0]
		const formView = new SingleFormfieldDialog({
			model: filter,
			heading: i18n.get('dashboards.datasets.editFilter.withField',
				this.model.getFieldLabel(fieldId)),
			fieldName: 'isAnyFilter',
			primaryButtonLabel: 'common.done',
			selectOptions: {}, // we're setting the options below but this is needed to render the select
			onSubmit: ({isAnyFilter}) => {
				const newFilter = _.cloneDeep(filter)
				newFilter.rollupIsAnyFilters[treeId] = isAnyFilter
				this._updateTemporaryFilter(filter, newFilter)
				this.trigger('edit-filter', isAnyFilter, filter)
				this.render()
				formView.trigger('done')
			}
		})
		appDisplay.dialog(formView)
		let selectedValue = rollupIsAnyFilters[treeId]
		rollupFilterDropdown(formView.$('select'), selectedValue, {
			isPermissionFilter: true
		})
	},

	_editFilterUsingWizard: function (filters, oldFilter) {
		var wizard = new FilterWizard(_.extend(this._wizardOptions(), {
			filters,
			// #51890 should not be able to change the field for filter widgets
			lockField: !!oldFilter?.filterWidget,
			filter: oldFilter,
			// #26085 filters that come from a dashboard filter widget are
			// allowed to be incomplete
			allowIncomplete: oldFilter.filterWidget,
			allLinkedFields: this.allLinkedFields,
			includeEditFields: this.includeEditFields,
			rollupPermissions: this.options.showPermissionFilters
		})).start()
		wizard.on('done', (newFilter, treeItems) => {
			newFilter.treeItems = treeItems
			this.trigger('edit-filter', newFilter, oldFilter, treeItems)
			this.updateTreeItems(treeItems)
			this._updateTemporaryFilter(oldFilter, newFilter)
			this.render()
		})
	},

	/**
	 * update the filter in this.filters so that if the dialog is cancelled
	 * they are not applied back to the model.
	 */
	_updateTemporaryFilter: function (oldFilter, newFilter) {
		_.each(this.filters, (candidate, i) => {
			// look for the original unedited filter and replace it with the
			// new one
			if (candidate == oldFilter) {
				this.filters[i] = newFilter
				return false
			}
		})
	},

	_wizardOptions() {
		return {
			model: this.model,
			treeItemsById: this.treeItemsById,
			showAggregationFilters: this.showAggregationFilters,
			showPermissionFilters: this.options.showPermissionFilters,
			allowAppCalendar: this.options.allowAppCalendar,
			allowAllTime: this.options.allowAllTime,
			parentDomainType: this.options.parentDomainType,
			datasetsById: this.getDatasetsById()
		}
	},

	/**
	 * Returns the filter a user clicked on
	 */
	getFilterForEvent: function(e) {
		const filterEl = $(e.currentTarget).closest('.filter')
		const index = _.findIndex(this.$('.filter'), (el) => filterEl[0] == el)
		return this.filteredFilters[index]
	},

	// update this.options.treeItems to include any tree labels that are newly
	// relevant due to addition of a rollup filter
	updateTreeItems: function(treeItems) {
		_.each(treeItems, function(treeItem) {
			this.treeItemsById[treeItem.id] = treeItem
		}.bind(this))
	},

	deleteFilter: function(e) {
		// don't allow the click to leak through and trigger editing a filter!
		e.stopPropagation()

		if (!this.isEditable) {
			return
		}

		var filter = this.getFilterForEvent(e)
		_.remove(this.filters, (f) => {
			return f == filter
		})
		this.trigger('delete-filter', filter)
		this.render()
	}

})

export default DatasetFilterBadges

DatasetFilterBadges.__filename__ = 'datasets/filters/datasets-filter-badges.js'
